import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Button,
  MenuItem,
  FormControl,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormHelperText,
  Typography,
  Stack,
  Box,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { getRiskAssessment } from "../../../../shared/api";
import { getFloorOptions } from "../../../helpers";
import GoogleAutocomplete from "../../../../shared/lib/googleAutocomplete";
import {
  parseCoordinatePair,
  parseEgsa87Pair,
} from "../../../../shared/helpers/parseCoordinates";
import RiskItem from "../riskItem";
import AssessmentMetaFields from "../../../shared/AssessmentMetaFields";
import { useAssessmentPdfExport } from "../history/useAssessmentPdfExport";
import { MAP_STYLE } from "../../constants";
import StreetViewAction from "../../../shared/StreetViewAction";

import styles from "../sideMenu.module.css";

const DEFAULT_ERRORS = {
  floor: "",
  place: "",
  latitude: "",
  longitude: "",
  egsa87x: "",
  egsa87y: "",
  form: "",
};

function AssessmentTabContent({
  mapRef,
  setMarkerInfo,
  formValues,
  setFormValues,
  selectedEarthquakeLayers,
}) {
  const [riskData, setRiskData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState(DEFAULT_ERRORS);
  const [visibleAddress, setVisibleAddress] = useState("");
  const { t, i18n } = useTranslation();
  const {
    exportAssessmentPdf,
    activeExportKey,
    errorMessage: pdfErrorMessage,
    clearError: clearPdfError,
    exportScopeCurrent,
  } = useAssessmentPdfExport();

  const getCurrentMapStyle = () => {
    const map = mapRef?.current?.getMap?.();
    const currentSprite = map?.getStyle?.()?.sprite || "";
    return currentSprite.includes("satellite")
      ? MAP_STYLE.satellite
      : MAP_STYLE.street;
  };

  const {
    place,
    floor,
    latitude,
    longitude,
    egsa87x,
    egsa87y,
    name,
    notes,
    activeAddressType,
    showResults,
  } = formValues;

  const clearErrors = () => {
    setErrors(DEFAULT_ERRORS);
  };

  const getErrorText = (value) => {
    if (!value) return "";
    return value.startsWith("errors.") ? t(value) : value;
  };

  const handleCoordinatePaste = (e) => {
    const text = e.clipboardData?.getData("text") ?? "";
    const parsed = parseCoordinatePair(text);
    if (!parsed) return;
    e.preventDefault();
    setFormValues((prev) => ({
      ...prev,
      latitude: parsed.latitude,
      longitude: parsed.longitude,
    }));
    setErrors((prev) => ({ ...prev, latitude: "", longitude: "" }));
  };

  const handleEgsa87Paste = (e) => {
    const text = e.clipboardData?.getData("text") ?? "";
    const parsed = parseEgsa87Pair(text);
    if (!parsed) return;
    e.preventDefault();
    setFormValues((prev) => ({
      ...prev,
      egsa87x: parsed.egsa87x,
      egsa87y: parsed.egsa87y,
    }));
    setErrors((prev) => ({ ...prev, egsa87x: "", egsa87y: "" }));
  };

  const resetFormAndRiskValues = () => {
    setFormValues((prev) => ({
      ...prev,
      floor: "ΙΣΟΓΕΙΟ",
      latitude: "",
      longitude: "",
      egsa87x: "",
      egsa87y: "",
      name: "",
      notes: "",
      place: null,
      showResults: false,
    }));
    clearErrors();

    setMarkerInfo(null);
    setRiskData(null);
    clearPdfError();
  };

  const flyToAddress = async ({ nextRiskData, address }) => {
    function flyToAndWait(map, opts, waitFor = "moveend") {
      return new Promise((resolve) => {
        const done = () => resolve();
        map.once(waitFor, done);
        map.flyTo(opts);
      });
    }

    const { longitude: lng, latitude: lat } = nextRiskData;
    const map = mapRef.current?.getMap();
    if (!map) return;

    setMarkerInfo(null);
    await flyToAndWait(map, { center: [lng, lat], zoom: 15 });
    setMarkerInfo({ address, open: true, riskData: nextRiskData });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearErrors();
    clearPdfError();

    const nextErrors = {};
    if (!floor) nextErrors.floor = "errors.floorRequired";

    if (activeAddressType === "address") {
      if (!place) nextErrors.place = "errors.addressRequired";
    } else if (activeAddressType === "coordinates") {
      if (!latitude) nextErrors.latitude = "errors.latitudeRequired";
      if (!longitude) nextErrors.longitude = "errors.longitudeRequired";
    } else {
      if (!egsa87x) nextErrors.egsa87x = "errors.egsa87xRequired";
      if (!egsa87y) nextErrors.egsa87y = "errors.egsa87yRequired";
    }

    if (Object.keys(nextErrors).length) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return;
    }

    const address = place?.address;
    const trimmedName = name?.trim() || undefined;
    const trimmedNotes = notes?.trim() || undefined;
    const payload = {
      floor,
      language: i18n.language,
      name: trimmedName,
      notes: trimmedNotes,
      ...(activeAddressType === "address"
        ? {
            address,
            latitude: place.lat,
            longitude: place.lng,
          }
        : activeAddressType === "coordinates"
          ? { latitude, longitude }
          : { egsa87x, egsa87y }),
    };

    try {
      setIsLoading(true);
      const data = await getRiskAssessment(payload);
      setIsLoading(false);

      setRiskData({ ...data, name: trimmedName, notes: trimmedNotes });
      setFormValues((prev) => ({
        ...prev,
        name: "",
        notes: "",
        showResults: true,
      }));

      let nextVisibleAddress;
      if (activeAddressType === "address") {
        nextVisibleAddress = `${address} (${place.lat}, ${place.lng})`;
      } else if (activeAddressType === "coordinates") {
        nextVisibleAddress = `${latitude}, ${longitude}`;
      } else {
        nextVisibleAddress = `${egsa87x}, ${egsa87y} (${data.latitude}, ${data.longitude})`;
      }
      setVisibleAddress(nextVisibleAddress);

      flyToAddress({
        nextRiskData: data,
        address: nextVisibleAddress,
      });
    } catch (error) {
      setIsLoading(false);

      if (error.error_code === "no_access") {
        setErrors((prev) => ({
          ...prev,
          form: "errors.wrongPrivateKey",
        }));
        return;
      }

      if (error.error_code === "invalid_address") {
        if (activeAddressType === "address") {
          setErrors((prev) => ({ ...prev, place: "errors.invalidAddress" }));
        } else if (activeAddressType === "coordinates") {
          setErrors((prev) => ({
            ...prev,
            latitude: "errors.invalidCoordinates",
            longitude: "errors.invalidCoordinates",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            egsa87x: "errors.invalidCoordinates",
            egsa87y: "errors.invalidCoordinates",
          }));
        }
        return;
      }

      if (error.error_code === "unsupported_location") {
        if (activeAddressType === "address") {
          setErrors((prev) => ({
            ...prev,
            place: "errors.addressOutsideGreece",
          }));
        } else if (activeAddressType === "coordinates") {
          setErrors((prev) => ({
            ...prev,
            latitude: "errors.coordinatesOutsideGreece",
            longitude: "errors.coordinatesOutsideGreece",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            egsa87x: "errors.coordinatesOutsideGreece",
            egsa87y: "errors.coordinatesOutsideGreece",
          }));
        }
        return;
      }

      if (error.error_code === "exceeded_limit") {
        setErrors((prev) => ({
          ...prev,
          form: "errors.tooManyRequests",
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        form: "errors.fetchRiskData",
      }));
    }
  };

  if (riskData && showResults) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "26px" }}>
        <Stack direction="column" spacing={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography>{visibleAddress}</Typography>
            <StreetViewAction
              latitude={riskData.latitude}
              longitude={riskData.longitude}
            />
          </Box>

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={resetFormAndRiskValues}
          >
            {t("risksMap.newRiskAssessment")}
          </Button>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() =>
              exportAssessmentPdf({
                assessment: { ...riskData, floor },
                address: visibleAddress,
                creationTime: new Date().toISOString(),
                mapStyle: getCurrentMapStyle(),
                earthquakeLayers: selectedEarthquakeLayers,
                scopeKey: exportScopeCurrent,
              })
            }
            disabled={activeExportKey === exportScopeCurrent}
          >
            {activeExportKey === exportScopeCurrent
              ? t("risksMap.pdf.exporting")
              : t("risksMap.pdf.download")}
          </Button>

          {pdfErrorMessage ? (
            <FormHelperText error>{pdfErrorMessage}</FormHelperText>
          ) : null}
        </Stack>

        <Divider />

        <RiskItem
          name="Fire"
          key="fire"
          risk={riskData.fire.risk}
          message={riskData.fire.message}
          description={riskData.fire.description}
          descriptionDict={riskData.fire.description_dict}
          riskType="fire"
        />
        <RiskItem
          name="Flood"
          key="flood"
          risk={riskData.flood.risk}
          message={riskData.flood.message}
          description={riskData.flood.description}
          descriptionDict={riskData.flood.description_dict}
          riskType="flood"
        />
        <RiskItem
          name="Earthquake"
          key="earthquake"
          risk={riskData.earthquake.risk}
          message={riskData.earthquake.message}
          description={riskData.earthquake.description}
          descriptionDict={riskData.earthquake.description_dict}
          riskType="earthquake"
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <TextField
        select
        label={t("forms.floor")}
        variant="outlined"
        fullWidth
        size="small"
        value={floor}
        onChange={(e) =>
          setFormValues((prev) => ({
            ...prev,
            floor: e.target.value,
          }))
        }
        error={Boolean(errors.floor)}
        helperText={getErrorText(errors.floor)}
        disabled={isLoading}
      >
        {getFloorOptions(t).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <ToggleButtonGroup
        value={activeAddressType}
        exclusive
        onChange={(e, value) => {
          if (value) {
            setFormValues((prev) => ({
              ...prev,
              place: null,
              activeAddressType: value,
            }));
            setErrors((prev) => ({
              ...prev,
              place: "",
              latitude: "",
              longitude: "",
              egsa87x: "",
              egsa87y: "",
              form: "",
            }));
          }
        }}
        size="small"
        color="primary"
        disabled={isLoading}
      >
        <ToggleButton value="address">{t("forms.address")}</ToggleButton>
        <ToggleButton value="coordinates">
          {t("forms.coordinates")}
        </ToggleButton>
        <ToggleButton value="egsa87">{t("forms.egsa87")}</ToggleButton>
      </ToggleButtonGroup>

      {activeAddressType === "address" ? (
        <FormControl error={Boolean(errors.place)} fullWidth>
          <GoogleAutocomplete
            placeholder={t("risksMap.addressPlaceholder")}
            disabled={isLoading}
            onLocationSelect={(locationData) =>
              setFormValues((prev) => ({
                ...prev,
                place: locationData,
              }))
            }
          />
          {errors.place && (
            <FormHelperText>{getErrorText(errors.place)}</FormHelperText>
          )}
        </FormControl>
      ) : activeAddressType === "coordinates" ? (
        <>
          <TextField
            name="latitude"
            label={t("forms.latitude")}
            variant="outlined"
            fullWidth
            size="small"
            value={latitude}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                latitude: e.target.value,
              }))
            }
            type="number"
            onPaste={handleCoordinatePaste}
            error={Boolean(errors.latitude)}
            helperText={getErrorText(errors.latitude)}
            disabled={isLoading}
          />
          <TextField
            name="longitude"
            label={t("forms.longitude")}
            variant="outlined"
            fullWidth
            size="small"
            value={longitude}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                longitude: e.target.value,
              }))
            }
            type="number"
            onPaste={handleCoordinatePaste}
            error={Boolean(errors.longitude)}
            helperText={getErrorText(errors.longitude)}
            disabled={isLoading}
          />
          <FormHelperText
            sx={{ mt: "-4px", display: "flex", alignItems: "center", gap: 1 }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            {t("forms.pasteCoordinatesHint")}
          </FormHelperText>
        </>
      ) : (
        <>
          <TextField
            name="egsa87x"
            label={t("forms.egsa87x")}
            variant="outlined"
            fullWidth
            size="small"
            value={egsa87x}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                egsa87x: e.target.value,
              }))
            }
            type="number"
            onPaste={handleEgsa87Paste}
            error={Boolean(errors.egsa87x)}
            helperText={getErrorText(errors.egsa87x)}
            disabled={isLoading}
          />
          <TextField
            name="egsa87y"
            label={t("forms.egsa87y")}
            variant="outlined"
            fullWidth
            size="small"
            value={egsa87y}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                egsa87y: e.target.value,
              }))
            }
            type="number"
            onPaste={handleEgsa87Paste}
            error={Boolean(errors.egsa87y)}
            helperText={getErrorText(errors.egsa87y)}
            disabled={isLoading}
          />
          <FormHelperText
            sx={{ mt: "-4px", display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            {t("forms.pasteEgsa87Hint")}
          </FormHelperText>
        </>
      )}

      <AssessmentMetaFields
        name={name}
        notes={notes}
        onNameChange={(value) =>
          setFormValues((prev) => ({ ...prev, name: value }))
        }
        onNotesChange={(value) =>
          setFormValues((prev) => ({ ...prev, notes: value }))
        }
        disabled={isLoading}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={isLoading}
      >
        {isLoading ? t("common.loading") : t("home.getRiskAssessment")}
      </Button>

      {errors.form ? (
        <FormHelperText error>{getErrorText(errors.form)}</FormHelperText>
      ) : null}
    </form>
  );
}

export default AssessmentTabContent;
